import { useEffect } from 'react';
import { Box } from "@mui/material";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';

import PrincipalVendedor from "./Vendedor/containers/Principal";
import PrincipalAdministrador from "./Admin/containers/PrincipalAdministrador";
import Login from './Login/containers/Login';
import Header from './Principal/containers/Header';
import PrincipalPage from './Principal/containers/Inicio';
import ProfileSelection from './Principal/containers/ProfileSelection';
import AppBackground from './Principal/components/AppBackground';
import AdminDashboard from "./Administracion/containers/AdminDashboard";
import RegistrarVentaPage from "./Vendedor/containers/RegistrarVentaPage";
import PostventaContainer from "./Postventa/containers/PostventaContainer";
import DatosContainer from "./Datos/containers/DatosContainer";


import { useUser } from './context/UserContext'; // 👈 El nuevo cerebro
import { logout as apiLogout } from "./auth"; // Solo para la llamada a la API
import socket from './socket';

function AppRoutes() {
  const navigate = useNavigate();
  // Consumimos el estado global
  const {
    isAuthenticated,
    isProfileSelected,
    userRole,
    empresa,
    perfil,
    logoutTotal,
    closeProfile
  } = useUser();
  const hasAdminAccess = isProfileSelected && userRole === "Admin";

  // --- LÓGICA DE SOCKET ---
  useEffect(() => {
    // 1. Base Connection & Company Room
    if (isAuthenticated && empresa) {
      socket.io.opts.query = {
        empresa_id: empresa.id
      };
      socket.auth = {
        empresa_id: Number(empresa.id),
        user_id: perfil?.id ? Number(perfil.id) : undefined,
        rol: perfil?.rol,
      };
      if (!socket.connected) {
        socket.connect();
      }
      socket.emit("join_company", {
        empresa_id: Number(empresa.id)
      });
    }

    // 2. Profile Specific Rooms
    if (isAuthenticated && isProfileSelected && perfil) {
      socket.emit("join_profile", {
        user_id: Number(perfil.id),
        user_rol: String(perfil.rol),
        empresa_id: Number(empresa.id)
      });
    }
    // 3. Re-join on reconnect and connection lifecycle logging
    const onConnect = () => {
      // on connect, rejoin rooms
      try {
        if (isAuthenticated && empresa) {
          socket.emit("join_company", { empresa_id: Number(empresa.id) });
        }
        if (isAuthenticated && isProfileSelected && perfil) {
          socket.emit("join_profile", {
            user_id: Number(perfil.id),
            user_rol: String(perfil.rol),
            empresa_id: Number(empresa.id)
          });
        }
      } catch (e) {
        console.error("[SOCKET] error during onConnect handler", e);
      }
    };

    const onDisconnect = (reason) => {
      // disconnected - no action required here
    };

    socket.on && socket.on("connect", onConnect);
    socket.on && socket.on("disconnect", onDisconnect);

    // 4. Limpiar / Salir de las salas de perfil y handlers
    return () => {
      socket.off && socket.off("connect", onConnect);
      socket.off && socket.off("disconnect", onDisconnect);
    };
  }, [isAuthenticated, isProfileSelected, empresa, perfil]);

  // --- MANEJADORES DE LOGOUT ---

  const handleLogoutFull = async () => {
    // 1. Llamada a API (si es necesaria)
    await apiLogout();
    // 2. Limpieza de estado global (esto redirige a /login automáticamente)
    logoutTotal();
  };

  const handleSwitchProfile = () => {
    // 1. Emitir el evento leave antes de borrar los datos
    if (perfil && empresa) {
      socket.emit("leave_profile", {
        user_id: Number(perfil.id),
        user_rol: String(perfil.rol),
        empresa_id: Number(empresa.id)
      });
    }
    // 2. Borrar el estado del perfil 
    closeProfile();
  };

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <AppBackground>
      <Routes>
        {/* Nivel 1: Selección de Módulo */}
        <Route path="/" element={<Navigate to="/inicio" replace />} />
        <Route path="/inicio" element={<PrincipalPage onLogout={handleLogoutFull} />} />

        {/* Nivel 2: Selección de Perfil */}
        <Route path="/ventas" element={<ProfileSelection />} />
        <Route path="/admin" element={
          hasAdminAccess ? (
            <AdminDashboard
              onLogout={handleLogoutFull}
              onSwitchProfile={handleSwitchProfile}
            />
          ) : (
            <Navigate to="/inicio" replace />
          )
        } />
        <Route
          path="/postventa"
          element={
            hasAdminAccess ? (
              <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
                <Header
                  moduleName="POSVENTA"
                  disableNotifications
                  onLogout={handleLogoutFull}
                  onSwitchProfile={handleSwitchProfile}
                  onBackToMenu={() => navigate('/inicio')}
                  hideUserInfoInMenu
                  sx={{ position: "sticky", top: 0, zIndex: 1100, bgcolor: "background.paper" }}
                />
                <Box sx={{ flex: 1, overflow: "hidden" }}>
                  <PostventaContainer />
                </Box>
              </Box>
            ) : (
              <Navigate to="/inicio" replace />
            )
          }
        />
        <Route
          path="/datos"
          element={
            hasAdminAccess ? (
              <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
                <Header
                  moduleName="DATOS"
                  disableNotifications
                  onLogout={handleLogoutFull}
                  onSwitchProfile={handleSwitchProfile}
                  onBackToMenu={() => navigate('/inicio')}
                  hideUserInfoInMenu
                  sx={{ position: "sticky", top: 0, zIndex: 1100, bgcolor: "background.paper" }}
                />
                <Box sx={{ flex: 1, overflow: "hidden" }}>
                  <DatosContainer />
                </Box>
              </Box>
            ) : (
              <Navigate to="/inicio" replace />
            )
          }
        />

        <Route
          path="/ventas/tablero/registrar-venta/:idVenCli"
          element={
            isProfileSelected && userRole !== "Admin" ? (
              <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
                <Header
                  onLogout={handleLogoutFull}
                  onSwitchProfile={handleSwitchProfile}
                  sx={{ position: "sticky", top: 0, zIndex: 1100, bgcolor: "background.paper" }}
                />
                <Box sx={{ flex: 1, overflow: "auto" }}>
                  <RegistrarVentaPage />
                </Box>
              </Box>
            ) : (
              <Navigate to="/ventas/tablero" replace />
            )
          }
        />

        {/* Nivel 3: TABLERO */}
        <Route
          path="/ventas/tablero/*"
          element={
            // Aquí la redirección es instantánea porque isProfileSelected es reactivo
            isProfileSelected ? (
              <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
                <Header
                  onLogout={handleLogoutFull}
                  onSwitchProfile={handleSwitchProfile} // Pasamos la función nueva
                  sx={{ position: "sticky", top: 0, zIndex: 1100, bgcolor: "background.paper" }}
                />
                <Box sx={{ flex: 1, overflow: "auto" }}>
                  {userRole === "Admin" ? <PrincipalAdministrador /> : <PrincipalVendedor />}
                </Box>
              </Box>
            ) : (
              <Navigate to="/ventas" replace />
            )
          }
        />

        {/* Redirecciones */}
        <Route path="/login" element={<Navigate to="/inicio" replace />} />
      </Routes>
    </AppBackground>
  );
}

export default function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}
